import React, { useState, useEffect, useCallback } from 'react';
import { getAuth } from 'firebase/auth';
import { useTranslation } from 'react-i18next';
import { AppState, AppAction } from '@/types';
import { functionsBaseUrl } from '@/config/functionsUrl';
import { showToast } from '@/components/ui/Toast';
import { Car, Plus, X, Gauge, ChevronRight } from 'lucide-react';
import { EmptyState } from '@/components/ui/EmptyState';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { VehicleProfile, ExtendedVehicle, FUEL_LABELS } from './VehicleProfile';

// ── API helper ─────────────────────────────────────────────────────────────────

async function apiFetch(path: string, method: string, body?: object): Promise<any> {
  const user = getAuth().currentUser;
  if (!user) throw new Error('Not authenticated');
  const token = await user.getIdToken();
  const res = await fetch(`${functionsBaseUrl}/apiVehicles${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok && res.status !== 204) {
    const err = await res.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(err.error ?? 'Request failed');
  }
  if (res.status === 204) return null;
  return res.json();
}

const FUEL_TYPES = ['gas', 'diesel', 'electric', 'hybrid'];

interface VehiclesProps {
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
}

// ── Vehicles list view ─────────────────────────────────────────────────────────

export const Vehicles: React.FC<VehiclesProps> = ({ state }) => {
  const { t } = useTranslation();
  const householdId = state.currentHouseholdId;
  const [vehicles, setVehicles] = useState<ExtendedVehicle[]>([]);
  const [selectedVehicle, setSelectedVehicle] = useState<ExtendedVehicle | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Add vehicle modal
  const [showAddVehicle, setShowAddVehicle] = useState(false);
  const [newVehicleName, setNewVehicleName] = useState('');
  const [newVehicleMake, setNewVehicleMake] = useState('');
  const [newVehicleModel, setNewVehicleModel] = useState('');
  const [newVehicleYear, setNewVehicleYear] = useState('');
  const [newVehicleFuel, setNewVehicleFuel] = useState('gas');
  const [saving, setSaving] = useState(false);

  const loadVehicles = useCallback(async () => {
    if (!householdId) return;
    try {
      setLoading(true);
      const res = await apiFetch(`?householdId=${householdId}`, 'GET');
      setVehicles(res.data ?? []);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [householdId]);

  useEffect(() => { loadVehicles(); }, [loadVehicles]);

  const handleAddVehicle = async () => {
    if (!householdId || !newVehicleName.trim()) return;
    setSaving(true);
    try {
      const res = await apiFetch('', 'POST', {
        householdId,
        name: newVehicleName.trim(),
        fuelType: newVehicleFuel,
        make: newVehicleMake.trim() || undefined,
        model: newVehicleModel.trim() || undefined,
        year: newVehicleYear ? parseInt(newVehicleYear) : undefined,
      });
      setVehicles(prev => [...prev, res.data]);
      setShowAddVehicle(false);
      setNewVehicleName('');
      setNewVehicleMake('');
      setNewVehicleModel('');
      setNewVehicleYear('');
      setNewVehicleFuel('gas');
    } catch (e: any) { showToast(e.message, 'error'); } finally { setSaving(false); }
  };

  const handleDeleteVehicle = async (e: React.MouseEvent, vehicle: ExtendedVehicle) => {
    e.stopPropagation();
    if (!householdId || !window.confirm(t('vehicles.confirm_delete', 'Delete {{name}}?', { name: vehicle.name }))) return;
    try {
      await apiFetch(`/${vehicle.id}?householdId=${householdId}`, 'DELETE');
      setVehicles(prev => prev.filter(v => v.id !== vehicle.id));
      if (selectedVehicle?.id === vehicle.id) setSelectedVehicle(null);
    } catch (e: any) { showToast(e.message, 'error'); }
  };

  // If a vehicle is selected, show its full profile
  if (selectedVehicle) {
    return (
      <VehicleProfile
        vehicle={selectedVehicle}
        householdId={householdId!}
        onBack={() => setSelectedVehicle(null)}
        onUpdate={updated => {
          setVehicles(prev => prev.map(v => v.id === updated.id ? updated : v));
          setSelectedVehicle(updated);
        }}
      />
    );
  }

  if (!householdId) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-slate-400">{t('vehicles.no_household', 'No household selected.')}</p>
      </div>
    );
  }

  const inputCls = 'w-full bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 ring-brand/40';

  return (
    <div className="max-w-page-full mx-auto p-4 md:p-6 space-y-6 pb-32 animate-in fade-in duration-500">

      {/* Header */}
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black tracking-tighter text-slate-900 dark:text-white">{t('vehicles.title', 'Vehicles')}</h1>
          <p className="text-2xs font-black text-brand uppercase tracking-[0.3em] mt-0.5">
            {t('vehicles.subtitle', 'Household Garage')}
          </p>
        </div>
        <button
          onClick={() => setShowAddVehicle(true)}
          className="bg-brand text-white p-4 rounded-full shadow-xl shadow-brand/20 hover:scale-105 active:scale-95 transition-all"
        >
          <Plus className="w-5 h-5" />
        </button>
      </header>

      {/* List */}
      {loading ? (
        <div className="flex justify-center py-16">
          <LoadingSpinner size="md" />
        </div>
      ) : error ? (
        <p className="text-rose-500 text-sm">{error}</p>
      ) : vehicles.length === 0 ? (
        <EmptyState
          icon={<Car className="w-8 h-8 text-slate-400" />}
          title={t('vehicles.empty_title', 'No vehicles yet')}
          description={t('vehicles.empty_desc', 'Add your first vehicle to track mileage, service, bookings, and more.')}
        >
          <button
            onClick={() => setShowAddVehicle(true)}
            className="px-5 py-2.5 bg-brand text-white text-xs font-black uppercase tracking-widest rounded-full hover:bg-brand-hover transition-colors"
          >
            {t('vehicles.add_first', 'Add First Vehicle')}
          </button>
        </EmptyState>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {vehicles.map(v => (
            <button
              key={v.id}
              onClick={() => setSelectedVehicle(v)}
              className="text-left bg-white dark:bg-slate-900 rounded-[2rem] border border-slate-100 dark:border-slate-800 p-7 hover:shadow-xl hover:border-brand/30 hover:-translate-y-0.5 transition-all group relative"
            >
              {/* Delete */}
              <button
                onClick={e => handleDeleteVehicle(e, v)}
                className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 p-2 rounded-full text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-900/20 transition-all"
              >
                <X className="w-3.5 h-3.5" />
              </button>

              <div className="flex items-start justify-between mb-4">
                <Car className="w-8 h-8 text-brand" />
                <span className="text-2xs font-black uppercase tracking-widest px-2 py-1 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-500">
                  {FUEL_LABELS[v.fuelType] ?? v.fuelType}
                </span>
              </div>

              <h3 className="font-black text-xl text-slate-900 dark:text-white tracking-tight">
                {v.name}
              </h3>

              {(v.make || v.model || v.year) && (
                <p className="text-2xs font-black uppercase tracking-widest text-slate-400 mt-1">
                  {[v.year, v.make, v.model].filter(Boolean).join(' ')}
                </p>
              )}

              {/* Quick stats */}
              <div className="flex items-center gap-3 mt-4 pt-4 border-t border-slate-100 dark:border-slate-800">
                {v.currentMileage && (
                  <span className="flex items-center gap-1 text-2xs text-slate-400 font-medium">
                    <Gauge className="w-3 h-3" />{v.currentMileage.toLocaleString()} mi
                  </span>
                )}
                {v.licensePlate && (
                  <span className="text-2xs text-slate-400 font-medium">{v.licensePlate}</span>
                )}
                <ChevronRight className="w-4 h-4 text-brand ml-auto flex-shrink-0 group-hover:translate-x-0.5 transition-transform" />
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Add Vehicle Modal */}
      {showAddVehicle && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-md" onClick={() => setShowAddVehicle(false)} />
          <div className="relative bg-white dark:bg-slate-950 rounded-[2rem] p-8 w-full max-w-md shadow-2xl space-y-4 mx-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-black tracking-tight text-slate-900 dark:text-white">{t('vehicles.add_title', 'Add Vehicle')}</h2>
              <button onClick={() => setShowAddVehicle(false)} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors">
                <X className="w-4 h-4 text-slate-400" />
              </button>
            </div>

            <input
              className={inputCls}
              placeholder={t('vehicles.name_placeholder', 'Vehicle name *')}
              value={newVehicleName}
              onChange={e => setNewVehicleName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleAddVehicle()}
            />
            <div className="grid grid-cols-2 gap-3">
              <input className={inputCls} placeholder={t('vehicles.make_placeholder', 'Make (e.g. Toyota)')} value={newVehicleMake} onChange={e => setNewVehicleMake(e.target.value)} />
              <input className={inputCls} placeholder={t('vehicles.model_placeholder', 'Model (e.g. Camry)')} value={newVehicleModel} onChange={e => setNewVehicleModel(e.target.value)} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input className={inputCls} placeholder={t('vehicles.year_placeholder', 'Year (e.g. 2022)')} value={newVehicleYear} onChange={e => setNewVehicleYear(e.target.value)} />
              <select className={inputCls} value={newVehicleFuel} onChange={e => setNewVehicleFuel(e.target.value)}>
                {FUEL_TYPES.map(f => <option key={f} value={f}>{FUEL_LABELS[f] ?? f}</option>)}
              </select>
            </div>

            <div className="flex gap-3 pt-1">
              <button
                onClick={() => setShowAddVehicle(false)}
                className="flex-1 py-3 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-500 text-sm font-bold hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
              >
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                onClick={handleAddVehicle}
                disabled={!newVehicleName.trim() || saving}
                className="flex-1 py-3 rounded-xl bg-brand text-white text-sm font-bold hover:bg-brand-hover disabled:opacity-50 transition-colors"
              >
                {saving ? t('common.saving', 'Saving...') : t('vehicles.add_title', 'Add Vehicle')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
